/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: MohSelect.tsx
 🗓️ Created: 2026-03-16
 🧩 Description:
    Reusable dropdown for selecting a Music on Hold (MOH) class.
    Fetches all pages from /api/telephony/moh-sources, deduplicates
    by class_name, and always includes a 'default' option.
    Matches the logic used in the Extensions > Advanced Tab.

 🔁 Last Updated: 2026-03-16 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect } from 'react';
import { buildApiUrl, getApiHeaders } from '../../config/api';

interface MohSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  /** If true, always shows "None" as the first option (in addition to Default). Default: false */
  allowNone?: boolean;
}

export default function MohSelect({
  value,
  onChange,
  placeholder = 'Select Music on Hold...',
  className = '',
  disabled = false,
  hasError = false,
  allowNone = false,
}: MohSelectProps) {
  const [mohClasses, setMohClasses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const classes = new Set<string>();
        let page = 1;
        const limit = 100;
        let hasMore = true;

        while (hasMore) {
          const res = await fetch(
            buildApiUrl(`/api/telephony/moh-sources?page=${page}&limit=${limit}&enabled=true&sortBy=class_name&sortOrder=asc`),
            { method: 'GET', headers: getApiHeaders() }
          );

          if (res.ok) {
            const result = await res.json();
            if (result.success && Array.isArray(result.data)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              result.data.forEach((moh: any) => {
                if (moh.class_name && typeof moh.class_name === 'string') {
                  classes.add(moh.class_name.trim());
                }
              });
              const pagination = result.pagination;
              if (pagination?.totalPages && page < pagination.totalPages) {
                page++;
              } else {
                hasMore = false;
              }
            } else {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        // Always include 'default', then the rest sorted
        const allClasses = new Set<string>(['default']);
        Array.from(classes).forEach((c) => allClasses.add(c));
        setMohClasses(Array.from(allClasses).sort());
      } catch {
        setMohClasses(['default']);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  return (
    <select
      value={value || (allowNone ? '' : 'default')}
      onChange={(e) => onChange?.(e.target.value)}
      disabled={disabled || isLoading}
      className={`w-full px-3 py-2 bg-background border ${hasError ? 'border-red-500' : 'border-input'} rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {isLoading ? (
        <option value="">Loading...</option>
      ) : (
        <>
          {allowNone && <option value="">— {placeholder} —</option>}
          {mohClasses.map((c) => (
            <option key={c} value={c}>
              {c === 'default' ? 'Default' : c}
            </option>
          ))}
        </>
      )}
    </select>
  );
}
